import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ShoppingCart, Minus, Plus, ChevronLeft, Truck, Shield, RotateCcw, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useCart } from "../App";
import ProductReviews from "../components/ProductReviews";
import axios from "axios";
import { API } from "../App";

const ProductPage = () => {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [reviewStats, setReviewStats] = useState({ average: 0, total: 0 });
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const [productRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/products/${productId}`),
          axios.get(`${API}/products/${productId}/reviews`)
        ]);
        setProduct(productRes.data);
        setReviewStats({
          average: reviewsRes.data.average_rating || 0,
          total: reviewsRes.data.total_reviews || 0
        });
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  const handleAddToCart = async () => {
    await addToCart(productId, quantity);
  };

  const discountedPrice = product?.discount_percent > 0
    ? product.price * (1 - product.discount_percent / 100)
    : product?.price;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center" data-testid="product-not-found">
        <h1 className="text-2xl text-gray-900 mb-4">Product Not Found</h1>
        <Link to="/shop">
          <Button className="bg-primary hover:bg-primary/90">Back to Shop</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 animate-fade-in" data-testid="product-page">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <Link to="/shop" className="inline-flex items-center text-gray-500 hover:text-primary mb-8 transition-colors" data-testid="back-link">
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Shop
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-white border border-gray-200 rounded-lg overflow-hidden">
              <img
                src={product.images?.[selectedImage] || "https://via.placeholder.com/600x600?text=No+Image"}
                alt={product.name}
                className="w-full h-full object-contain p-8"
                data-testid="main-product-image"
              />
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 border rounded-lg overflow-hidden transition-all ${
                      selectedImage === index ? 'border-primary border-2' : 'border-gray-200 hover:border-gray-400'
                    }`}
                    data-testid={`thumbnail-${index}`}
                  >
                    <img src={img} alt={`${product.name} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            {/* Brand & Category */}
            <div className="flex items-center gap-2 mb-2">
              <span className="text-primary font-medium uppercase tracking-wider text-sm">{product.brand}</span>
              <span className="text-gray-300">|</span>
              <span className="text-gray-500 text-sm capitalize">{product.category}</span>
            </div>

            {/* Title */}
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 uppercase tracking-tight" data-testid="product-name">
              {product.name}
            </h1>

            {/* Rating Summary */}
            {reviewStats.total > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-4 h-4 ${star <= Math.round(reviewStats.average) ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-200 text-gray-200'}`}
                    />
                  ))}
                </div>
                <span className="text-gray-600 text-sm">
                  {reviewStats.average.toFixed(1)} ({reviewStats.total} review{reviewStats.total !== 1 ? 's' : ''})
                </span>
              </div>
            )}

            {/* SKU */}
            <p className="text-gray-400 text-sm mt-2">SKU: {product.sku}</p>

            {/* Position / Side Display */}
            {(product.position || product.side) && (
              <div className="flex flex-wrap gap-2 mt-3">
                {product.position && (
                  <span className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                    {product.position}
                  </span>
                )}
                {product.side && (
                  <span className="inline-flex items-center px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full">
                    {product.side}
                  </span>
                )}
              </div>
            )}

            {/* Price */}
            <div className="flex items-center gap-4 mt-6">
              {product.discount_percent > 0 && (
                <span className="text-gray-400 line-through text-xl">${product.price.toFixed(2)}</span>
              )}
              <span className="text-4xl font-bold text-gray-900" data-testid="product-price">
                ${discountedPrice?.toFixed(2)}
              </span>
              {product.discount_percent > 0 && (
                <span className="badge-discount">{product.discount_percent}% OFF</span>
              )}
            </div>

            {/* Stock */}
            <div className="mt-4">
              {product.stock > 0 ? (
                <span className="text-green-600 font-medium flex items-center gap-2" data-testid="stock-status">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  In Stock ({product.stock} available)
                </span>
              ) : (
                <span className="text-red-500 font-medium" data-testid="stock-status">Out of Stock</span>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-600 mt-6 leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Quantity & Add to Cart */}
            <div className="flex items-center gap-4 mt-8">
              <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="px-4 py-3 text-gray-600 hover:bg-gray-100 transition-colors"
                  data-testid="quantity-minus"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 text-center py-3 text-gray-900 border-x border-gray-300 bg-white"
                  min="1"
                  data-testid="quantity-input"
                />
                <button
                  onClick={() => setQuantity(q => q + 1)}
                  className="px-4 py-3 text-gray-600 hover:bg-gray-100 transition-colors"
                  data-testid="quantity-plus"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <Button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider py-6 text-lg"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                Add to Cart
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-gray-200">
              <div className="flex flex-col items-center text-center">
                <div className="p-3 bg-primary/10 rounded-lg mb-2">
                  <Truck className="w-6 h-6 text-primary" />
                </div>
                <span className="text-gray-600 text-sm">Free Shipping</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="p-3 bg-primary/10 rounded-lg mb-2">
                  <Shield className="w-6 h-6 text-primary" />
                </div>
                <span className="text-gray-600 text-sm">2 Year Warranty</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <div className="p-3 bg-primary/10 rounded-lg mb-2">
                  <RotateCcw className="w-6 h-6 text-primary" />
                </div>
                <span className="text-gray-600 text-sm">30 Day Returns</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="specs" className="mt-16">
          <TabsList className="bg-white border border-gray-200 p-1">
            <TabsTrigger value="specs" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="specs-tab">
              Specifications
            </TabsTrigger>
            <TabsTrigger value="compatibility" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="compatibility-tab">
              Vehicle Compatibility
            </TabsTrigger>
            <TabsTrigger value="reviews" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="reviews-tab">
              Reviews ({reviewStats.total})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="specs" className="mt-6" data-testid="specs-content">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-heading text-xl font-bold text-gray-900 uppercase mb-4">Technical Specifications</h3>
              {product.specifications && Object.keys(product.specifications).length > 0 ? (
                <table className="w-full">
                  <tbody>
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <tr key={key} className="border-b border-gray-100 last:border-0">
                        <td className="py-3 text-gray-500 capitalize">{key.replace(/_/g, ' ')}</td>
                        <td className="py-3 text-gray-900 text-right font-medium">{String(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500">No specifications available.</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="compatibility" className="mt-6" data-testid="compatibility-content">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-heading text-xl font-bold text-gray-900 uppercase mb-4">Compatible Vehicles</h3>
              {product.compatible_vehicles && product.compatible_vehicles.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="py-3 text-left text-gray-500 font-medium">Year(s)</th>
                      <th className="py-3 text-left text-gray-500 font-medium">Make</th>
                      <th className="py-3 text-left text-gray-500 font-medium">Model</th>
                      <th className="py-3 text-left text-gray-500 font-medium">Engine</th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.compatible_vehicles.map((vehicle, index) => (
                      <tr key={index} className="border-b border-gray-100 last:border-0">
                        <td className="py-3 text-gray-900">
                          {vehicle.year_from && vehicle.year_to 
                            ? `${vehicle.year_from} - ${vehicle.year_to}`
                            : vehicle.year_from || vehicle.year_to || vehicle.year || "All Years"}
                        </td>
                        <td className="py-3 text-gray-900 font-medium">{vehicle.make}</td>
                        <td className="py-3 text-gray-900">{vehicle.model}</td>
                        <td className="py-3 text-gray-600">{vehicle.engine || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-gray-500">This product fits a wide range of vehicles. Contact us for specific fitment information.</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="mt-6" data-testid="reviews-content">
            <ProductReviews productId={productId} productName={product.name} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProductPage;
